import type { Viewer } from '@puppycad/cad-core';
import * as THREE from 'three';
import type { ViewportScreenPoint, ViewportScreenRect } from './types';

/** 世界坐标投影复用向量，避免 rAF 热路径持续分配。 */
const projectedPointRef = new THREE.Vector3();

/** 支持刷新投影矩阵的相机类型，Viewer 当前相机实现提供该能力。 */
type ProjectionMatrixCamera = THREE.Camera & {
    updateProjectionMatrix: () => void;
};

/**
 * 将三维场景世界坐标映射到叠加层局部坐标。
 *
 * 设计说明：
 * 1. 先经相机投影得到视口容器内局部坐标，再按 DOM 矩形偏移换算到 overlay 坐标系；
 * 2. 点在视锥外（NDC z 超出 [-1, 1]）时返回 null，由调用方决定是否隐藏引线；
 * 3. 与浮窗定位共用同一投影链路，保证引线起点与 3D 锚点同帧对齐。
 *
 * @param viewer Viewer 实例（提供相机）。
 * @param worldPoint 世界坐标。
 * @param viewportContainer 视口容器。
 * @param overlayRoot 叠加层根节点。
 * @returns 叠加层局部坐标；不可投影时返回 null。
 */
export function mapWorldPointToOverlayLocal(
    viewer: Viewer,
    worldPoint: THREE.Vector3,
    viewportContainer: HTMLElement,
    overlayRoot: HTMLElement,
): ViewportScreenPoint | null {
    const camera = viewer.camera;
    if (!camera) {
        return null;
    }

    const viewportRect = viewportContainer.getBoundingClientRect();
    const overlayRect = overlayRoot.getBoundingClientRect();
    if (viewportRect.width <= 0 || viewportRect.height <= 0) {
        return null;
    }

    camera.updateMatrixWorld(true);
    (camera as ProjectionMatrixCamera).updateProjectionMatrix();
    const projected = projectedPointRef.copy(worldPoint).project(camera);
    if (projected.z < -1 || projected.z > 1) {
        return null;
    }

    const viewportLocalX = ((projected.x + 1) * 0.5) * viewportRect.width;
    const viewportLocalY = ((1 - projected.y) * 0.5) * viewportRect.height;

    return {
        x: viewportRect.left - overlayRect.left + viewportLocalX,
        y: viewportRect.top - overlayRect.top + viewportLocalY,
    };
}

/**
 * 将元素客户端矩形映射到叠加层局部坐标。
 *
 * @param element 目标元素。
 * @param overlayRoot 叠加层根节点。
 * @returns 叠加层局部矩形。
 */
export function mapElementRectToOverlayLocal(
    element: HTMLElement,
    overlayRoot: HTMLElement,
): ViewportScreenRect {
    const elementRect = element.getBoundingClientRect();
    const overlayRect = overlayRoot.getBoundingClientRect();
    return {
        left: elementRect.left - overlayRect.left,
        top: elementRect.top - overlayRect.top,
        right: elementRect.right - overlayRect.left,
        bottom: elementRect.bottom - overlayRect.top,
    };
}
